import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Button,
  InputAdornment,
  MenuItem,
  Select as MuiSelect,
  FormControl,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { transactionService } from 'src/services/transaction.service';
import CustomPagination from 'src/components/CustomPagination';
import SearchNotFound from 'src/components/SearchNotFound';
import CreatedBy from 'src/components/CreatedBy';
import { useAuth } from 'src/context/auth/AuthContext';
import { useDebounce } from 'src/hooks/useDebounce';

const ROWS_PER_PAGE = 8;

export function TransactionTypes(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getBaseRoute, hasPermission } = useAuth();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit'>('all');

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: transactionTypesResponse, isLoading } = useQuery({
    queryKey: ['transactionTypes', { page, rowsPerPage, type: typeFilter === 'all' ? undefined : typeFilter, search: debouncedSearch }],
    queryFn: () =>
      transactionService.getTransactionTypes(
        page,
        rowsPerPage,
        typeFilter === 'all' ? undefined : typeFilter,
        debouncedSearch || undefined
      ),
    staleTime: 10000,
  });

  const typeList = transactionTypesResponse?.data || [];
  const totalCount = transactionTypesResponse?.total || 0;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => transactionService.deleteTransactionType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactionTypes'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-reasons'] });
      toast.success('Transaction type deleted successfully');
      handleCloseDeleteDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete transaction type');
    },
  });

  const handleDelete = (id: string) => {
    setSelectedTypeId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedTypeId) {
      deleteMutation.mutate(selectedTypeId);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedTypeId(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const canManage = hasPermission('transaction_types.manage');

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Controls Bar: Search & Type Filter */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          bgcolor: 'background.neutral',
        }}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search transaction types by name or details..."
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="solar:magnifer-linear" width={18} style={{ color: '#94A3B8' }} />
              </InputAdornment>
            ),
            sx: {
              height: 36,
              bgcolor: 'background.paper',
              borderRadius: 1.5,
              fontSize: '0.8125rem',
              maxWidth: { xs: '100%', md: 320 },
            },
          }}
        />

        {/* Filter Chips & Actions */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <FormControl size="small">
            <MuiSelect
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as 'all' | 'credit' | 'debit');
                setPage(1);
              }}
              sx={{
                height: 34,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: 1.25,
                bgcolor: 'background.paper',
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                All Categories
              </MenuItem>
              <MenuItem value="credit" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="solar:card-recive-bold-duotone" width={16} style={{ color: '#059669' }} />
                  <span>Credits (Earnings / Rewards)</span>
                </Stack>
              </MenuItem>
              <MenuItem value="debit" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="solar:card-send-bold-duotone" width={16} style={{ color: '#DC2626' }} />
                  <span>Debits (Expenses / Deductions)</span>
                </Stack>
              </MenuItem>
            </MuiSelect>
          </FormControl>

          {canManage && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
              onClick={() => navigate(getBaseRoute('/add-transaction-type'))}
              sx={{
                height: 34,
                px: 2,
                fontWeight: 600,
                fontSize: '0.8125rem',
                borderRadius: 1.25,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
              }}
            >
              New Type
            </Button>
          )}
        </Stack>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <thead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Transaction Type
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                In or Out
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Description / Guidelines
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Price
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Created By
              </TableCell>
              <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Actions
              </TableCell>
            </TableRow>
          </thead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.8125rem' }}>
                    Loading transaction types catalog...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : typeList.length > 0 ? (
              typeList.map((type: any) => {
                const isCredit = type.type === 'credit';
                const hasPoints = type?.points !== undefined && type?.points !== null && type?.points !== 0;
                const pointsValue = Number(type?.points || 0);

                return (
                  <TableRow
                    key={type.id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    {/* Name with Duotone Icon */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            color: isCredit ? '#059669' : '#DC2626',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                            width={19}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                            {type.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Flow Category Chip */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={
                          <Icon
                            icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                            width={13}
                            color={isCredit ? '#059669' : '#DC2626'}
                          />
                        }
                        label={isCredit ? 'Money in' : 'Money out'}
                        size="small"
                        sx={{
                          height: 25,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: 1.25,
                          bgcolor: isCredit ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: isCredit ? '#059669' : '#DC2626',
                          border: '1px solid',
                          borderColor: isCredit ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                        }}
                      />
                      {!isCredit && (
                        <Chip
                          label={type.charge_mode === 'bill' ? 'Bill' : 'Auto-charge'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 25, ml: 0.75, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1.25 }}
                        />
                      )}
                    </TableCell>

                    {/* Description */}
                    <TableCell sx={{ py: 2, maxWidth: 360 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8125rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontWeight: 400,
                        }}
                      >
                        {type.description || 'No description recorded'}
                      </Typography>
                    </TableCell>

                    {/* Default Amount */}
                    <TableCell sx={{ py: 2 }}>
                      {hasPoints ? (
                        <Chip
                          label={`${isCredit ? '+' : '-'}$${pointsValue.toFixed(2)}`}
                          size="small"
                          sx={{
                            height: 25,
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            borderRadius: 1.25,
                            bgcolor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            color: isCredit ? '#059669' : '#DC2626',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontWeight: 500 }}>
                          Any amount
                        </Typography>
                      )}
                    </TableCell>

                    {/* Created By */}
                    <TableCell sx={{ py: 2 }}>
                      <CreatedBy creator={type.creator} />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit Type">
                          <IconButton
                            size="small"
                            onClick={() => navigate(getBaseRoute(`/edit-transaction-type/${type.id}`))}
                            sx={{
                              p: 0.75,
                              color: 'text.secondary',
                              '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                            }}
                          >
                            <Icon icon="solar:pen-bold-duotone" width={17} />
                          </IconButton>
                        </Tooltip>
                        {canManage && (
                          <Tooltip title="Delete Type">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(type.id)}
                              sx={{
                                p: 0.75,
                                color: 'text.secondary',
                                '&:hover': { bgcolor: 'error.lighter', color: 'error.main' },
                              }}
                            >
                              <Icon icon="solar:trash-bin-trash-bold-duotone" width={17} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 4 }}>
                  <SearchNotFound searchQuery={debouncedSearch} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <CustomPagination
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[8, 10, 25]}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1.125rem' }}>
          Delete Transaction Type?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete this transaction type? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCloseDeleteDialog}
            sx={{ fontWeight: 600, borderRadius: 1.25, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            sx={{ fontWeight: 600, borderRadius: 1.25, textTransform: 'none' }}
          >
            {deleteMutation.isPending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
